import { create } from 'zustand';

/**
 * Client-side presentation state for admin-published announcements. The
 * server owns the durable "active for this driver" list and the PERMANENT
 * dismissal (POST /me/announcements/:id/dismiss). This store owns the two
 * lighter, client-only notions the split dismissal semantics need:
 *
 *  - sessionDismissed: closing the OVERLAY (its X) hides it for this tab
 *    session only. The announcement stays in the bell and returns as an
 *    overlay next time the app is opened — closing the overlay is "not now",
 *    not "never". Backed by sessionStorage so a same-tab reload stays quiet,
 *    but a fresh app open (new session) re-presents it.
 *
 *  - bellSeen: which announcements the driver has already seen listed in the
 *    bell. Drives the unread bump — a newly published announcement lights the
 *    badge until the bell is opened. Backed by localStorage so the badge
 *    doesn't re-alert across sessions for something already seen (a permanent
 *    dismissal drops it from the server list entirely, so it never returns).
 *
 *  - forcedId: a re-present request from the bell. Tapping an announcement row
 *    routes to the map and sets this; the overlay then shows THAT announcement
 *    regardless of sessionDismissed. Cleared when the overlay closes.
 */

const SESSION_KEY = 'evi.announcement.sessionDismissed';
const BELL_SEEN_KEY = 'evi.announcement.bellSeen';

function readIds(storage: Storage | undefined, key: string): string[] {
  if (!storage) return [];
  try {
    const raw = storage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

function writeIds(storage: Storage | undefined, key: string, ids: string[]): void {
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify(ids));
  } catch {
    // Private mode / quota — in-memory state still holds for this tab.
  }
}

const session = typeof window !== 'undefined' ? window.sessionStorage : undefined;
const local = typeof window !== 'undefined' ? window.localStorage : undefined;

interface AnnouncementsState {
  sessionDismissed: string[];
  bellSeen: string[];
  forcedId: string | null;
  /** Whether the auto overlay has already fired THIS app open. In-memory
   *  only (deliberately NOT persisted), so it resets on every fresh load /
   *  app open: the top announcement re-pops as an overlay each time the
   *  driver opens the app, until they permanently dismiss it from the bell.
   *  Within a single load it stays true so the card doesn't re-pop as they
   *  navigate around the app. */
  autoShown: boolean;
  /** Overlay X — hide for this session only (no server call). */
  dismissForSession: (id: string) => void;
  /** Re-present a specific announcement as an overlay (from the bell). */
  present: (id: string) => void;
  /** Clear the re-present request once the overlay has consumed it. */
  clearForced: () => void;
  /** Record that the once-per-session auto overlay has fired. */
  markAutoShown: () => void;
  /** Mark announcements as seen-in-bell so they stop bumping the badge. */
  markBellSeen: (ids: string[]) => void;
}

export const useAnnouncementsStore = create<AnnouncementsState>((set, get) => ({
  sessionDismissed: readIds(session, SESSION_KEY),
  bellSeen: readIds(local, BELL_SEEN_KEY),
  forcedId: null,
  // In-memory only: a fresh app open starts with no auto overlay fired, so
  // the top announcement re-presents itself every time the app is opened.
  autoShown: false,
  dismissForSession: (id) => {
    if (get().sessionDismissed.includes(id)) return;
    const next = [...get().sessionDismissed, id];
    writeIds(session, SESSION_KEY, next);
    set({ sessionDismissed: next });
  },
  present: (id) => {
    // A deliberate re-present also counts as "a card was shown this load",
    // so closing it never triggers an unrequested auto-pop of the next one.
    set({ forcedId: id, autoShown: true });
  },
  clearForced: () => set({ forcedId: null }),
  markAutoShown: () => {
    if (get().autoShown) return;
    set({ autoShown: true });
  },
  markBellSeen: (ids) => {
    const current = get().bellSeen;
    const fresh = ids.filter((id) => !current.includes(id));
    if (fresh.length === 0) return;
    const next = [...current, ...fresh];
    writeIds(local, BELL_SEEN_KEY, next);
    set({ bellSeen: next });
  },
}));
